import { getI18nRuntimeLocale, tr } from '../i18n/runtime';
import { TimeoutError } from './withTimeout';

/**
 * Текст ошибки для человека — вместо сырого `e.message`.
 *
 * <p><b>Зачем.</b> Около тридцати обработчиков показывали `e instanceof Error ? e.message : …`.
 * В `message` при этом бывает что угодно: «Network request failed» от fetch, «Direct upload
 * rejected by storage (403)», английская фраза сервера вроде «Only group admin can delete this
 * chat» — посреди русского интерфейса (аудит удобства 2026-10-04).
 *
 * <p><b>Как решает.</b> Сетевой сбой и таймаут — своей фразой «нет связи». Текст сервера,
 * который мы уже перевели (`errorFromBody` подбирает слова своим словарём), проходит как есть.
 * Сырой английский текст при неанглийском языке приложения заменяется на `fallback` — фразу
 * про то самое действие, которую знает вызывающий («Не удалось отправить заявку»). Сырой текст
 * при этом не теряется: он уходит в отчёт об ошибке тем же обработчиком, если тот его шлёт.
 *
 * @param fallback уже переведённая фраза про действие, которое не удалось
 */
export function humanizeError(error: unknown, fallback: string): string {
  if (error instanceof TimeoutError) return tr('errorNetwork');
  // Оборванный по таймауту fetch: `AbortError` с текстом «Aborted» (UX-52). Это та же «нет
  // связи» — запрос не дождался ответа.
  if (isAbortError(error)) return tr('errorNetwork');
  const message = messageOf(error);
  if (!message) return fallback;
  if (NETWORK.test(message)) return tr('errorNetwork');
  if (/\b(429|too many requests)\b/i.test(message)) return tr('errorTooManyRequests');
  if (/\b(502|503|504)\b|bad gateway|service unavailable|gateway time-?out/i.test(message)) {
    return tr('errorServerUnavailable');
  }
  if (looksTechnical(message)) return fallback;
  if (looksLikeRawServerEnglish(message)) return fallback;
  return message;
}

/**
 * Английская фраза сервера при неанглийском интерфейсе. Для русского — просто «нет кириллицы».
 * Сербский и французский сами пишутся латиницей, и наш же перевод по этому признаку не
 * отличить — там узнаём английский по служебным словам, из которых сервер и строит фразы.
 */
function looksLikeRawServerEnglish(message: string): boolean {
  const locale = getI18nRuntimeLocale();
  if (locale === 'en') return false;
  if (locale === 'ru') return /[A-Za-z]/.test(message) && !/[Ѐ-ӿ]/.test(message);
  return /^[\x20-\x7E]+$/.test(message) && ENGLISH_WORDS.test(message);
}

const ENGLISH_WORDS =
  /\b(the|is|are|not|cannot|can't|only|invalid|failed|must|already|denied|allowed|found|required|too|you|this)\b/i;

const NETWORK =
  /network request failed|failed to fetch|networkerror|load failed|network error|the internet connection appears to be offline|ECONNREFUSED|ENOTFOUND|ETIMEDOUT/i;

/** `DOMException` в браузере и `Error` в RN — узнаём по имени, а не по классу. */
function isAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { name?: unknown }).name === 'AbortError'
  );
}

function messageOf(error: unknown): string {
  if (error instanceof Error) return error.message.trim();
  if (typeof error === 'string') return error.trim();
  return '';
}

/** Код статуса, имя исключения, стек, JSON — не для глаз человека. */
function looksTechnical(message: string): boolean {
  return (
    /^\s*[{[]/.test(message) ||
    /\b(exception|stack|undefined|null|TypeError|ReferenceError|SyntaxError)\b/.test(message) ||
    /\(\d{3}\)/.test(message) ||
    /^HTTP \d{3}/i.test(message)
  );
}
